import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Box, Paper, TextField, IconButton, Typography,
    List, ListItem, ListItemText, Drawer, 
     useTheme, useMediaQuery, AppBar, Toolbar, Accordion, AccordionSummary, AccordionDetails,
     CircularProgress, Fade, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button
} from '@mui/material';
import { Send as SendIcon, Menu as MenuIcon, ExpandMore as ExpandMoreIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark, materialLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { API_URL } from "../lib/utils";

// Utility function to detect if text is predominantly RTL
const isRTLText = (text) => {
    // RTL Unicode ranges: Hebrew (0x0590-0x05FF), Arabic (0x0600-0x06FF), Arabic Supplement (0x0750-0x077F)
    const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/g;
    const rtlMatches = text.match(rtlRegex);
    const rtlCount = rtlMatches ? rtlMatches.length : 0;
    
    // Count only letters and numbers for total character count
    const letterRegex = /[\p{L}\p{N}]/gu;
    const letterMatches = text.match(letterRegex);
    const totalLetters = letterMatches ? letterMatches.length : 0;
    
    if (totalLetters === 0) return false;
    
    // Return true if at least 50% of characters are RTL
    return (rtlCount / totalLetters) >= 0.5;
};

// Move components outside to prevent recreation on every render
const MarkdownMessage = React.memo(({ content, themeMode }) => {
    const isRTL = isRTLText(content);
    
    return (
        <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ textAlign: isRTL ? 'right' : 'left' }}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                            <SyntaxHighlighter
                                style={themeMode === 'dark' ? materialDark : materialLight}
                                language={match[1]}
                                PreTag="div"
                                {...props}
                            >
                                {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                        ) : (
                            <code className={className} {...props}>
                                {children}
                            </code>
                        );
                    }
                }}
            >
                {content}
            </ReactMarkdown>
        </Box>
    );
});

// Loading indicator component
const LoadingMessage = React.memo(() => (
    <Fade in={true}>
        <Paper
            sx={{
                p: 2,
                mr: 'auto',
                maxWidth: '80%',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                gap: 2
            }}
        >
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
                Thinking...
            </Typography>
        </Paper>
    </Fade>
));

const Chat = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { conversationId } = useParams();
    const [conversations, setConversations] = useState([]);
    const [currentConversation, setCurrentConversation] = useState(null);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesLengthRef = useRef(0);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        fetchConversations().then(() => {
            if (conversationId) {
                fetchConversation(conversationId);
            }
        });
    }, [conversationId]);

    // Only scroll when messages are added, not on every conversation change
    useEffect(() => {
        const messageCount = currentConversation?.messages?.length || 0;
        if (messageCount > messagesLengthRef.current) {
            scrollToBottom();
            messagesLengthRef.current = messageCount;
        }
    }, [currentConversation?.messages?.length, scrollToBottom]);

    const fetchConversations = async () => {
        const response = await fetch(`${API_URL}/api/conversations`, {
            credentials: 'include',
            headers: {
                'Accept': 'application/json'
            }
        });
        const data = await response.json();
        setConversations(data);
    };

    const fetchConversation = async (id) => {
        const response = await fetch(`${API_URL}/api/conversations/${id}`, {
            credentials: 'include',
            headers: {
                'Accept': 'application/json'
            }
        });
        const data = await response.json();
        setCurrentConversation(data);
        messagesLengthRef.current = data.messages?.length || 0;
    };

    const handleSendMessage = useCallback(async () => {
        if (!message.trim() || !currentConversation) return;

        setLoading(true);
        const messageToSend = message;
        setMessage(''); // Clear input immediately for better UX
        
        try {
            const response = await fetch(`${API_URL}/api/conversations/${currentConversation._id}/messages`, {
                method: 'POST',
                credentials: 'include',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ content: messageToSend })
            });
            const { userMessage, assistantMessage } = await response.json();
            
            setCurrentConversation(prev => ({
                ...prev,
                messages: [...prev.messages, userMessage, assistantMessage]
            }));
        } catch (error) {
            console.error('Error sending message:', error);
            setMessage(messageToSend); // Restore message on error
        } finally {
            setLoading(false);
        }
    }, [message, currentConversation]);

    const handleClearHistory = useCallback(async () => {
        if (!currentConversation) return;
        
        try {
            const response = await fetch(`${API_URL}/api/conversations/${currentConversation._id}/messages`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { 
                    'Accept': 'application/json'
                }
            });
            
            if (response.ok) {
                const { messages } = await response.json();
                setCurrentConversation(prev => ({
                    ...prev,
                    messages: messages
                }));
                messagesLengthRef.current = messages.length;
            }
        } catch (error) {
            console.error('Error clearing history:', error);
        } finally {
            setClearDialogOpen(false);
        }
    }, [currentConversation]);

    const ConversationsList = useCallback(() => (
        <List>
            {conversations.map((conv) => (
                <ListItem 
                    key={conv._id}
                    button
                    selected={currentConversation?._id === conv._id}
                    onClick={() => {
                        fetchConversation(conv._id);
                        if (isMobile) setDrawerOpen(false);
                    }}
                >
                    <ListItemText 
                        primary={conv.title}
                        secondary={new Date(conv.createdAt).toLocaleDateString()}
                    />
                </ListItem>
            ))}
        </List>
    ), [conversations, currentConversation?._id, isMobile]);

    // Memoize rendered messages to prevent re-rendering on every keystroke
    const renderedMessages = useMemo(() => {
        if (!currentConversation?.messages) return null;
        
        return currentConversation.messages
            .filter(msg => msg.role !== 'system')
            .map((message, index) => (
                <Paper
                    key={index}
                    sx={{
                        p: 2,
                        ml: message.role === 'user' ? 'auto' : 0,
                        mr: message.role === 'assistant' ? 'auto' : 0,
                        maxWidth: '80%',
                        bgcolor: message.role === 'user' ? 
                            'primary.main' : 'background.paper',
                        color: message.role === 'user' ? 
                            'primary.contrastText' : 'text.primary'
                    }}
                >
                    <MarkdownMessage content={message.content} themeMode={theme.palette.mode} />
                </Paper>
            ));
    }, [currentConversation?.messages, theme.palette.mode]);

    // Memoize system messages accordion
    const systemMessagesAccordion = useMemo(() => {
        if (!currentConversation?.messages.some(msg => msg.role === 'system')) return null;
        
        return (
            <Accordion
                sx={{
                    bgcolor: 'background.paper',
                    '&:before': { display: 'none' },
                    '& .MuiCollapse-root': {
                        transition: 'height 200ms cubic-bezier(0.4, 0, 0.2, 1) !important'
                    }
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        opacity: 0.7,
                        '&:hover': { opacity: 1 }
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        System Context
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {currentConversation.messages
                        .filter(msg => msg.role === 'system')
                        .map((message, index) => (
                            <Paper
                                key={index}
                                sx={{
                                    p: 2,
                                    bgcolor: 'background.default',
                                    fontSize: '0.9em'
                                }}
                            >
                                <MarkdownMessage content={message.content} themeMode={theme.palette.mode} />
                            </Paper>
                        ))}
                </AccordionDetails>
            </Accordion>
        );
    }, [currentConversation?.messages, theme.palette.mode]);

    return (
        <Box sx={{ display: 'flex', height: '100vh' }}>
            {/* Sidebar for desktop */}
            {!isMobile && (
                <Paper 
                    sx={{ 
                        width: 300,
                        minWidth: 300,
                        borderRight: 1,
                        borderColor: 'divider',
                        overflow: 'auto'
                    }}
                >
                    <ConversationsList />
                </Paper>
            )}

            {/* Drawer for mobile */}
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': { width: 300 }
                }}
            >
                <ConversationsList />
            </Drawer>

            {/* Main chat area */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Mobile header with menu button */}
                {isMobile && (
                    <AppBar position="static" color="default">
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={() => setDrawerOpen(true)}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
                                {currentConversation?.title || 'Chat'}
                            </Typography>
                            <IconButton
                                color="inherit"
                                onClick={() => setClearDialogOpen(true)}
                                disabled={!currentConversation || currentConversation.messages?.filter(m => m.role !== 'system').length === 0}
                                title="Clear chat history"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                )}
                
                {/* Desktop header with clear button */}
                {!isMobile && currentConversation && (
                    <Box sx={{ 
                        p: 2, 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Typography variant="h6">
                            {currentConversation.title}
                        </Typography>
                        <IconButton
                            color="inherit"
                            onClick={() => setClearDialogOpen(true)}
                            disabled={currentConversation.messages?.filter(m => m.role !== 'system').length === 0}
                            title="Clear chat history"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                )}

                {/* Messages area */}
                <Box sx={{ 
                    flexGrow: 1, 
                    overflow: 'auto',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}>
                    {systemMessagesAccordion}
                    {renderedMessages}
                    {loading && <LoadingMessage />}
                    <div ref={messagesEndRef} />
                </Box>

                {/* Input area */}
                <Paper 
                    sx={{ 
                        p: 2,
                        borderTop: 1,
                        borderColor: 'divider'
                    }}
                    elevation={3}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Type your message..."
                            disabled={loading || !currentConversation}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                        />
                        <IconButton
                            onClick={handleSendMessage}
                            disabled={loading || !message.trim() || !currentConversation}
                            color="primary"
                        >
                            <SendIcon />
                        </IconButton>
                    </Box>
                </Paper>
            </Box>
            
            {/* Clear history confirmation dialog */}
            <Dialog
                open={clearDialogOpen}
                onClose={() => setClearDialogOpen(false)}
            >
                <DialogTitle>Clear Chat History?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will remove all conversation messages but keep the transcription context. 
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClearDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleClearHistory} color="error" variant="contained">
                        Clear History
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Chat;